// Os ícones da lateral foram com ela para `app-nav.ts` (TUT-227). O que
// sobrou aqui é o cromo do cabeçalho e do popover do perfil.
import {
  BriefcaseBusiness,
  ChevronDown,
  Moon,
  Sun,
} from "lucide-react";
import { useEffect, useState, type ComponentProps, type ReactNode } from "react";
import {
  Link,
  useLocation,
  useMatchRoute,
} from "@tanstack/react-router";
import { NotificationBell } from "./NotificationBell";
import { ChatPanelButton } from "./ChatPanelButton";
import { ChatRapido } from "./conversas/ChatRapido";
import { ChatRapidoProvider } from "./conversas/ChatRapidoProvider";
import { BuscaDoTopo } from "./paleta/BuscaDoTopo";
import { FotoOuIniciais } from "./usuarios/AvatarDaPessoa";
import { PaletaProvider } from "./paleta/PaletaProvider";
import { Wordmark } from "@/components/ui/wordmark";
import { cn } from "@/lib/utils";
import type { Session } from "../lib/types";
import { MaintenanceBanner } from "./MaintenanceBanner";
import { sidebarScrollbarClass } from "@/components/ui/atoms";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { mainContentClass } from "@/components/ui/atoms";
import { EscolhaDoJeitoDoMenu, escolherJeito } from "./escolha-do-menu";
import { GrupoRecolhido } from "./grupo-recolhido";
import { BarraInferior, respiroDaBarraInferior } from "./barra-inferior";
import { useMenuPreference } from "@/lib/useMenuPreference";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { api } from "@/lib/api";
import { useThemePreference } from "@/lib/useThemePreference";
import {
  profileMenuClass,
  profilePopoverClass,
  profilePopoverLinkClass,
  skipLinkClass,
} from "@/components/ui/chrome";
import {
  construirNavegacao,
  ehGrupo,
  construirNavegacaoDaConta,
  PISO_DA_LATERAL,
  rotasDoGrupo,
  type GrupoExpansivel,
} from "@/components/app-nav";
import { lerLocal, gravarLocal } from "@/lib/armazenamento-local";
import { fundoComManchasClass } from "@brevi/ui-kit/superficies";
import { MarcaDoEscritorio } from "@/components/MarcaDoEscritorio";
import { nomeDoEscritorio } from "@/lib/api/perfil-do-escritorio";
import { IconButton } from "@/components/ui/icon-button";

// TUT-227: a navegação virou DADO e mora em `app-nav.ts`. O que ficou aqui é
// só o estado de expansão dos grupos — e as rotas que o alimentam derivam da
// MESMA lista que a barra renderiza, em vez de serem uma segunda cópia.
//
// (A TUT-175 havia descido a Agenda para dentro de Processos, porque no
// primeiro nível ela não dizia DE QUE agenda se tratava. A cliente pediu o
// contrário em 10/09, e o motivo original caducou: com "Agendamento" ao lado,
// o par torna a distinção visível. O porquê completo está em `app-nav.ts`.)

const registryRoutes = rotasDoGrupo("cadastros");
const elaborationRoutes = rotasDoGrupo("elaboracao");
const consultingRoutes = rotasDoGrupo("consultoria");
const transportRoutes = rotasDoGrupo("transportes");
const financeRoutes = rotasDoGrupo("financeiro");

const storedGroupsKey = "tutelae-nav-groups";

// Deriva de `GrupoExpansivel`: grupo novo em `app-nav.ts` passa a exigir
// estado aqui pelo compilador, em vez de silenciosamente nascer fechado.
type NavGroups = Record<GrupoExpansivel, boolean>;

function initialGroups(pathname: string): NavGroups {
  try {
    const stored = lerLocal(storedGroupsKey);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "cadastros" in parsed
      ) {
        const groups = parsed as {
          cadastros: boolean;
          // Preferência gravada antes da TUT-133 não tem a chave nova — o
          // grupo estreante abre por padrão em vez de invalidar o resto. Vale
          // igual para `transportes`, que estreou na TUT-167 fatia 1, para
          // `financeiro`, agrupado na TUT-207l, e para `elaboracao`, que na
          // TUT-566 tomou o lugar de `processos` (a chave antiga é ignorada).
          elaboracao?: boolean;
          consultoria?: boolean;
          transportes?: boolean;
          financeiro?: boolean;
        };
        // A rota atual sempre força o grupo que a contém a abrir.
        return {
          elaboracao:
            (groups.elaboracao ?? true) ||
            elaborationRoutes.some((route) => pathname.startsWith(route)),
          consultoria:
            (groups.consultoria ?? true) ||
            consultingRoutes.some((route) => pathname.startsWith(route)),
          transportes:
            (groups.transportes ?? true) ||
            transportRoutes.some((route) => pathname.startsWith(route)),
          financeiro:
            (groups.financeiro ?? true) ||
            financeRoutes.some((route) => pathname.startsWith(route)),
          cadastros:
            groups.cadastros ||
            registryRoutes.some((route) => pathname.startsWith(route)),
        };
      }
    }
  } catch {
    // Preferência corrompida: volta ao padrão expandido.
  }
  return {
    elaboracao: true,
    consultoria: true,
    transportes: true,
    cadastros: true,
    financeiro: true,
  };
}

// A composição é a oficial do shadcn Sidebar; o catálogo de rotas, a ordem e a
// visibilidade por papel continuam sendo contrato do produto.
function SidebarLink({
  className,
  to,
  end,
  activeRoutes,
  label,
  sub = false,
  children,
}: {
  to: ComponentProps<typeof Link>["to"];
  end?: boolean;
  /** Outras rotas que também acendem o item (a outra vista da mesma tela). */
  activeRoutes?: readonly string[];
  className?: string;
  label: string;
  sub?: boolean;
  children: ReactNode;
}) {
  const matchRoute = useMatchRoute();
  const isActive =
    Boolean(matchRoute({ to, fuzzy: !end })) ||
    Boolean(
      activeRoutes?.some((route) =>
        matchRoute({ to: route as ComponentProps<typeof Link>["to"], fuzzy: true }),
      ),
    );
  const link = (
    <Link to={to} aria-current={isActive ? "page" : undefined}>
      {children}
    </Link>
  );

  if (sub) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={isActive} className={className}>
          {link}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={label}
        className={cn(
          "min-h-[2.92rem] gap-[0.9rem] rounded-lg px-[0.85rem]",
          className,
        )}
      >
        {link}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavGroup({
  to,
  end,
  icon: Icon,
  label,
  activeRoutes,
  expanded,
  onToggle,
  soIcones,
  children,
}: {
  /** Ausente: o grupo não tem página, e o cabeçalho só abre e fecha. */
  to?: ComponentProps<typeof Link>["to"];
  end?: boolean;
  icon: typeof BriefcaseBusiness;
  label: string;
  activeRoutes?: readonly string[];
  expanded: boolean;
  onToggle: () => void;
  /** TUT-472 — "Menu recolhido": os filhos abrem ao lado do ícone. */
  soIcones: boolean;
  children: ReactNode;
}) {
  const matchRoute = useMatchRoute();
  const location = useLocation();
  const { isMobile } = useSidebar();
  const naPagina = to ? Boolean(matchRoute({ to, fuzzy: !end })) : false;
  const isActive = activeRoutes
    ? activeRoutes.some((route) => location.pathname.startsWith(route))
    : naPagina;
  // Na página de um filho o grupo acende, mas "page" é só do link da página
  // aberta: o grupo diz `true` (é o caminho), e o filho, `page`.
  const atual = naPagina ? "page" : isActive ? "true" : undefined;
  if (soIcones && !isMobile) {
    return (
      <GrupoRecolhido
        icon={Icon}
        label={label}
        isActive={isActive}
        // TUT-478 — a página do grupo é o CABEÇALHO do ninho, não mais um
        // item da lista: o link em negrito, e os filhos recuados abaixo.
        pagina={
          to ? (
            <SidebarMenuSubButton asChild isActive={naPagina} className="font-semibold">
              <Link to={to} aria-current={naPagina ? "page" : undefined}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </Link>
            </SidebarMenuSubButton>
          ) : undefined
        }
      >
        {children}
      </GrupoRecolhido>
    );
  }
  if (!to) {
    // TUT-330 — grupo sem página: o cabeçalho INTEIRO é o botão de abrir e
    // fechar, com a seta dentro dele. Um segundo botão só com a seta, ao lado
    // de um cabeçalho que não leva a lugar nenhum, seria dois alvos para o
    // mesmo gesto.
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          type="button"
          isActive={isActive}
          tooltip={label}
          aria-expanded={expanded}
          onClick={onToggle}
          className="min-h-[2.92rem] gap-[0.9rem] rounded-lg px-[0.85rem]"
        >
          <Icon aria-hidden="true" />
          <span>{label}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn("ml-auto transition-transform", !expanded && "-rotate-90")}
          />
        </SidebarMenuButton>
        {expanded ? <SidebarMenuSub>{children}</SidebarMenuSub> : null}
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={label}
        className="min-h-[2.92rem] gap-[0.9rem] rounded-lg px-[0.85rem] pr-9"
      >
        <Link to={to} aria-current={atual}>
          <Icon aria-hidden="true" />
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
      <SidebarMenuAction
        type="button"
        aria-expanded={expanded}
        aria-label={expanded ? `Recolher o grupo ${label}` : `Expandir o grupo ${label}`}
        onClick={onToggle}
        // TUT-282 — size-6 (24px) era o piso EXATO do WCAG 2.5.8, sem folga
        // nenhuma; agora lê o token compacto (--control-h-compact, 32px). O
        // deslocamento de topo desce de top-2.5 (10px) para top-2 (8px) para
        // manter o botão centrado na linha de 2.92rem (46,7px) do grupo.
        className="top-2 size-[var(--control-h-compact)]"
      >
        <ChevronDown
          aria-hidden="true"
          className={cn("transition-transform", !expanded && "-rotate-90")}
        />
      </SidebarMenuAction>
      {expanded ? (
        <SidebarMenuSub>{children}</SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );
}

/**
 * TUT-478 (2a) — o nome do escritório saiu da lateral e mora na barra de
 * cima, como texto. No celular a barra cede o lugar à busca e o nome sobe
 * para o topo do "Mais" (a gaveta): só existe lá quando a lateral é gaveta.
 */
function NomeNoMais({ nome }: { nome: string }) {
  const { isMobile } = useSidebar();
  if (!isMobile) return null;
  return (
    <span
      data-shell="office-name"
      title={nome}
      className="block truncate px-1 text-corpo font-semibold text-sidebar-foreground"
    >
      {nome}
    </span>
  );
}

type PropsDoAppShell = {
  session: Session;
  /**
   * TUT-551 — a foto da ficha de quem está logado, para o menu da conta;
   * `null` ou ausente, as iniciais. Quem lê a ficha é o portão (`App.tsx`),
   * que tem o cliente de consultas: o cromo continua sem buscar nada.
   */
  fotoDaConta?: string | null;
  onLogout: () => void;
  children: ReactNode;
};

/**
 * TUT-496 — o cromo do escritório mais a paleta Ctrl+K, que mora em volta dele:
 * a busca da barra de cima a abre, e o atalho vale em qualquer tela.
 */
export function AppShell(props: PropsDoAppShell) {
  return (
    <PaletaProvider session={props.session}>
      <CromoDoEscritorio {...props} />
    </PaletaProvider>
  );
}

function CromoDoEscritorio({ session, fotoDaConta, onLogout, children }: PropsDoAppShell) {
  const { theme, toggleTheme } = useThemePreference();
  // TUT-153: a tela troca na hora (toggleTheme); a persistência no perfil é
  // fire-and-forget — falha de rede não desfaz a cor que o olho já viu.
  const alternarTemaDurable = () => {
    void api.setThemePreference(toggleTheme()).catch(
      () => {
        // Sem rede a preferência fica só neste navegador — o localStorage
        // segura; a próxima troca bem-sucedida regrava no perfil.
      },
    );
  };
  // TUT-465 — o JEITO do menu, por pessoa: o mesmo caminho do tema — a tela
  // muda na hora e a gravação no perfil é fire-and-forget.
  const { jeito } = useMenuPreference();
  const location = useLocation();
  const [groups, setGroups] = useState(() => initialGroups(location.pathname));
  // TUT-472 — o menu da conta é controlado: o `<Link>` navega no cliente sem
  // desmontar o shell, e o Radix só fecha em clique fora, Esc ou no gatilho —
  // o popover ficava aberto por cima da página nova.
  const [contaAberta, setContaAberta] = useState(false);
  // A lateral desta sessão, já na ordem do contrato e sem o que ela não pode
  // ver. O piso (Visão geral) é garantido lá dentro — ver `app-nav.ts`.
  const navegacao = construirNavegacao(session);

  useEffect(() => {
    gravarLocal(storedGroupsKey, JSON.stringify(groups));
  }, [groups]);

  function toggleGroup(name: keyof NavGroups) {
    setGroups((current) => ({ ...current, [name]: !current[name] }));
  }

  return (
    // TUT-467 — o fundo com manchas é da TELA, e não de um bloco: o dono
    // reprovou as manchas presas atrás da Visão geral ("precisa estar no fundo
    // da tela, não do card"). Ele é o fundo da própria raiz do app, fixo na
    // janela (`bg-fixed`), e não rola com a página — como no BreviTI, a outra
    // casa que ele deu de referência. Fundo, e não camada: uma camada atrás de
    // tudo pedia `isolate` na raiz, e o contexto de empilhamento prendia o
    // aviso "Desfazer" do Kanban (z-80) abaixo do popover do cartão, que mora
    // num portal fora da raiz (a e2e `ui-audit` no celular pegou). Degradê
    // radial e não `blur`: custa uma pintura. O pico de cada mancha é o pior
    // caso que `contrast.test.ts` mede, para o texto direto sobre o fundo e
    // para o texto sobre o vidro.
    <div data-fundo-com-manchas="" className={cn("min-h-screen", fundoComManchasClass)}>
      <MaintenanceBanner />
      <a className={skipLinkClass} href="#main-content">
        Pular para o conteúdo
      </a>
      {/* TUT-465 — o trilho em PÍLULA do redesenho (TUT-463): flutua fora da
          borda (`variant="floating"`), no jeito escolhido por pessoa. TUT-521
          — dois jeitos: expandido (`texto`, o padrão) é o `open` do provider,
          e recolhido (`icones`) o fechado. O botão do pé, o do topo e o Ctrl+B
          alternam entre os dois, e cada troca fica lembrada. */}
      {/* TUT-590 — o chat rápido (janelas, rascunhos, a caixa e o canal SSE
          da aba) vive aqui para sobreviver à navegação. */}
      <ChatRapidoProvider session={session}>
      <SidebarProvider
        open={jeito === "texto"}
        onOpenChange={(aberto) => escolherJeito(aberto ? "texto" : "icones")}
      >
        <CloseMobileSidebarOnNavigation pathname={location.pathname} />
        <Sidebar
          aria-label="Navegação principal"
          data-shell="sidebar"
          variant="floating"
          collapsible="icon"
          compacto={jeito === "icones"}
          className="sidebar border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
        >
          <SidebarHeader className="gap-3 px-3 pt-5 pb-2 group-data-[collapsible=icon]:px-2">
            <div className="min-h-8 w-full">
              <span className="sr-only">Tutelae by Brevi</span>
              <Wordmark
                aria-hidden="true"
                surface="dark"
                className="w-[11.5rem] max-w-none group-data-[collapsible=icon]:hidden"
              />
              <img
                src="/brand/tutelae-v2/symbols/tutelae-simbolo-fundo-escuro.svg"
                alt=""
                aria-hidden="true"
                className="mx-auto hidden size-8 select-none group-data-[collapsible=icon]:block"
                draggable={false}
              />
            </div>
            <NomeNoMais nome={nomeDoEscritorio(session)} />
          </SidebarHeader>

          <SidebarContent className={sidebarScrollbarClass}>
            <SidebarGroup>
              <SidebarGroupContent>
                <nav id="primary-navigation" aria-label="Navegação principal">
                  {/* TUT-227 — coluna ÚNICA, na ordem de `navegacaoPrincipal`.
                      A cliente recusou o bloco de rodapé (31:23), então a
                      hierarquia mora na ordem: o trabalho vem antes do apoio,
                      sem separador que reparta a barra em dois lugares. */}
                  <SidebarMenu>
                    {navegacao.map((entrada) => {
                      const Icone = entrada.icon;

                      if (!ehGrupo(entrada)) {
                        return (
                          <SidebarLink
                            key={entrada.to}
                            to={entrada.to}
                            end={entrada.to === PISO_DA_LATERAL}
                            activeRoutes={entrada.rotasAtivas}
                            label={entrada.label}
                          >
                            <Icone aria-hidden="true" />
                            <span>{entrada.label}</span>
                          </SidebarLink>
                        );
                      }

                      return (
                        <NavGroup
                          key={entrada.chave}
                          to={entrada.to}
                          end={entrada.end}
                          icon={Icone}
                          label={entrada.label}
                          // TUT-472 — TODO grupo acende na página de um
                          // filho: só os que tinham `rotasAtivas` acendiam, e
                          // em /prazos nenhum ícone do trilho ficava ativo.
                          activeRoutes={rotasDoGrupo(entrada.chave)}
                          expanded={groups[entrada.chave]}
                          onToggle={() => toggleGroup(entrada.chave)}
                          soIcones={jeito === "icones"}
                        >
                          {entrada.filhos.map(({ to, label, icon: IconeFilho }) => (
                            <SidebarLink key={to} to={to} label={label} sub>
                              <IconeFilho aria-hidden="true" />
                              <span>{label}</span>
                            </SidebarLink>
                          ))}
                        </NavGroup>
                      );
                    })}
                  </SidebarMenu>
                </nav>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <EscolhaDoJeitoDoMenu jeito={jeito} aoEscolher={escolherJeito} />
          <SidebarRail />
        </Sidebar>

        <SidebarInset className="min-h-screen min-w-0 bg-transparent">
        {/* TUT-471 — a barra de cima não termina num corte reto no começo da
            página: ela segue POR BAIXO do menu lateral até a borda esquerda
            da tela (o `before:`, que herda o fundo e repete a borda), e a pílula
            do menu flutua por cima dela. Pedido do dono em 03/10. */}
        <header
          data-shell="command-bar"
          className="sticky top-0 z-30 flex min-h-16 items-center gap-3 border-b border-border bg-[color-mix(in_srgb,var(--background)_72%,transparent)] px-4 py-2 shadow-sm [backdrop-filter:blur(16px)] max-[720px]:min-h-[58px] max-[720px]:gap-1.5 max-[720px]:px-2 before:pointer-events-none before:absolute before:top-0 before:-bottom-px before:right-full before:w-screen before:border-b before:border-border before:bg-inherit"
        >
          <SidebarTrigger aria-controls="primary-navigation" className="size-10 shrink-0" />
          {/* TUT-478 (2a) — o escritório em que a pessoa está: texto, sem botão
              (não há troca de escritório aqui), truncado com o nome inteiro no
              `title`. Era um bloco de contexto na lateral, nos três jeitos. */}
          {/* TUT-512 — o logo pequeno ao lado do nome (decisão 3 do estudo):
              a marca Tutelae da lateral não muda. O nome é o do perfil do
              escritório; o logo é decorativo aqui, porque o nome vem ao lado. */}
          <MarcaDoEscritorio
            session={session}
            nome={nomeDoEscritorio(session)}
            decorativa
            className="h-7 max-w-[6rem] shrink-0 max-[980px]:hidden"
          />
          <span
            data-shell="office-name"
            title={nomeDoEscritorio(session)}
            className="min-w-0 max-w-[10rem] shrink-0 truncate text-corpo font-semibold text-foreground min-[1200px]:max-w-[16rem] max-[980px]:hidden"
          >
            {nomeDoEscritorio(session)}
          </span>
          {/* TUT-296b — `min-w-0` virou incondicional: o ícone de conversas
              somou um 4º controle ao aglomerado da direita, e sob zoom 200%
              (mobile-layout.spec.ts) o campo de busca — sem min-width — não
              encolhia para abrir espaço, e o aglomerado vazava a viewport. */}
          {/* TUT-496 — a busca abre a paleta Ctrl+K (`BuscaDoTopo`). */}
          <search className="mx-auto min-w-0 w-full max-w-[42rem] max-[720px]:flex-auto">
            <BuscaDoTopo />
          </search>

          <div className="flex flex-[0_0_auto] items-center gap-1">
            {/* TUT-544 — sem grupo, só o Meu perfil: Conversas e o sino levam a telas que ela não alcança. */}
            {session.semGrupo ? null : <ChatPanelButton />}
            {session.semGrupo ? null : <NotificationBell />}
            <IconButton
              label={
                theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"
              }
              icone={theme === "dark" ? (
                <Sun aria-hidden="true" />
              ) : (
                <Moon aria-hidden="true" />
              )}
              onClick={alternarTemaDurable}
            />
            <Popover open={contaAberta} onOpenChange={setContaAberta}>
              <PopoverTrigger
                aria-label={`Menu de ${session.user.name}`}
                className={profileMenuClass}
              >
                <span className="overflow-hidden">
                  <FotoOuIniciais fotoUrl={fotoDaConta} iniciais={session.user.initials} />
                </span>
                <ChevronDown aria-hidden="true" />
              </PopoverTrigger>
              <PopoverContent
                className={profilePopoverClass}
                // Escolher um destino fecha o menu (os quatro links).
                onClick={(event) => {
                  if ((event.target as Element).closest("a")) setContaAberta(false);
                }}
              >
                <strong>{session.user.name}</strong>
                <small>{session.user.role}</small>
                {/* TUT-496 — os quatro links saíram do JSX e viraram DADO
                    (`navegacaoDaConta`, em `app-nav.ts`), para a paleta
                    Ctrl+K listar as mesmas telas.
                    TUT-192 — o perfil vira o HUB administrativo: o que
                    administra o escritório mora aqui, não na barra de
                    trabalho. TUT-336/337 — Usuários, Atividade e a Central de
                    Notificações viraram seções das Configurações (decisão do
                    dono em 27–28/09): o menu do perfil não repete o que mora
                    lá. TUT-192b — Ajuda e Feedbacks vêm para o mesmo menu que
                    o resto, fora do gate de papel porque são de todos. */}
                {construirNavegacaoDaConta(session).map(({ to, label, icon: Icone }) => (
                  <Link key={to} to={to} className={profilePopoverLinkClass}>
                    <Icone aria-hidden="true" />
                    {label}
                  </Link>
                ))}
                <button type="button" onClick={onLogout}>
                  Sair com segurança
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <main
          id="main-content"
          data-shell="content"
          className={cn(mainContentClass, respiroDaBarraInferior)}
          tabIndex={-1}
        >
          {children}
        </main>
        </SidebarInset>
        <BarraInferior navegacao={navegacao} />
        <ChatRapido />
      </SidebarProvider>
      </ChatRapidoProvider>
    </div>
  );
}

export function CloseMobileSidebarOnNavigation({ pathname }: { pathname: string }) {
  const { setOpenMobile } = useSidebar();
  useEffect(() => setOpenMobile(false), [pathname, setOpenMobile]);
  return null;
}
